import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import {
  fetchChecklists, fetchAllChecklists, fetchChecklist, createChecklist, updateChecklist,
  deleteChecklist, duplicateChecklist, copyChecklistTemplate, templatizeChecklist, setChecklistShares, reorderChecklists, createCategory, updateCategory,
  deleteCategory, reorderCategories, createItem, updateItem, deleteItem,
  reorderItems, replaceNcTemplates, createDataField, updateDataField, deleteDataField, reorderDataFields,
  assignChecklistScopes, removeChecklistScope,
  createChecklistRevision, fetchChecklistRevisions,
  type ChecklistParams, type CreateChecklistPayload, type UpdateChecklistPayload,
  type CreateRevisionPayload,
  type DuplicateChecklistPayload, type CreateCategoryPayload, type UpdateCategoryPayload,
  type ReorderPayload, type CreateItemPayload, type UpdateItemPayload,
  type ReplaceNcTemplatesPayload, type CreateDataFieldPayload, type UpdateDataFieldPayload,
} from "@/lib/checklists"

export const checklistKeys = {
  all: ["checklists"] as const,
  lists: () => [...checklistKeys.all, "list"] as const,
  list: (params: ChecklistParams) => [...checklistKeys.lists(), params] as const,
  details: () => [...checklistKeys.all, "detail"] as const,
  detail: (id: string) => [...checklistKeys.details(), id] as const,
  revisions: (id: string) => [...checklistKeys.all, "revisions", id] as const,
}

/** Bir listenin revizyon geçmişi — satırdaki ok açıldığında çekilir. */
export function useChecklistRevisions(id: string, enabled = true) {
  return useQuery({
    queryKey: checklistKeys.revisions(id),
    queryFn: () => fetchChecklistRevisions(id),
    enabled: !!id && enabled,
  })
}

/** Yeni revizyon oluştur: kopya güncel olur, önceki arşive düşer. */
export function useCreateChecklistRevision() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload?: CreateRevisionPayload }) =>
      createChecklistRevision(id, payload),
    onSuccess: () => {
      // Liste ve tüm revizyon geçmişleri tazelenir (aile durumu değişti).
      queryClient.invalidateQueries({ queryKey: checklistKeys.all })
    },
  })
}

export function useChecklists(params: ChecklistParams = {}) {
  return useQuery({
    queryKey: checklistKeys.list(params),
    queryFn: () => fetchChecklists(params),
  })
}

/** Sayfalanmamış tam liste — tarayıcı ekranındaki gruplama/sayaçlar için. */
export function useAllChecklists(params: Omit<ChecklistParams, "page" | "limit"> = {}) {
  return useQuery({
    queryKey: [...checklistKeys.lists(), "all", params] as const,
    queryFn: () => fetchAllChecklists(params),
  })
}

export function useChecklistDetail(id: string) {
  return useQuery({
    queryKey: checklistKeys.detail(id),
    queryFn: () => fetchChecklist(id),
    enabled: !!id,
  })
}

export function useCreateChecklist() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: CreateChecklistPayload) => createChecklist(payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: checklistKeys.lists() }),
  })
}

export function useUpdateChecklist(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: UpdateChecklistPayload) => updateChecklist(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: checklistKeys.lists() })
      queryClient.invalidateQueries({ queryKey: checklistKeys.detail(id) })
    },
  })
}

export function useDeleteChecklist() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => deleteChecklist(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: checklistKeys.lists() }),
  })
}

export function useDuplicateChecklist() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload?: DuplicateChecklistPayload }) =>
      duplicateChecklist(id, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: checklistKeys.lists() }),
  })
}

// Audex Sistem Şablonları
export function useChecklistTemplate() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => copyChecklistTemplate(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: checklistKeys.lists() }),
  })
}

// Bir listeden şablon üret (kopya).
export function useTemplatizeChecklist() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => templatizeChecklist(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: checklistKeys.lists() }),
  })
}

// Şablonun paylaşıldığı firmaları güncelle.
export function useSetChecklistShares() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, companyIds }: { id: string; companyIds: string[] }) =>
      setChecklistShares(id, companyIds),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: checklistKeys.lists() }),
  })
}

// Category mutations
export function useCreateCategory(checklistId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: CreateCategoryPayload) => createCategory(checklistId, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: checklistKeys.detail(checklistId) }),
  })
}

export function useUpdateCategory(checklistId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ categoryId, payload }: { categoryId: string; payload: UpdateCategoryPayload }) =>
      updateCategory(checklistId, categoryId, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: checklistKeys.detail(checklistId) }),
  })
}

export function useDeleteCategory(checklistId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (categoryId: string) => deleteCategory(checklistId, categoryId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: checklistKeys.detail(checklistId) }),
  })
}

export function useReorderChecklists() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: ReorderPayload) => reorderChecklists(payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: checklistKeys.lists() }),
  })
}

export function useReorderCategories(checklistId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: ReorderPayload) => reorderCategories(checklistId, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: checklistKeys.detail(checklistId) }),
  })
}

// Item mutations
export function useCreateItem(checklistId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ categoryId, payload }: { categoryId: string; payload: CreateItemPayload }) =>
      createItem(checklistId, categoryId, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: checklistKeys.detail(checklistId) }),
  })
}

export function useUpdateItem(checklistId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ itemId, payload }: { itemId: string; payload: UpdateItemPayload }) =>
      updateItem(checklistId, itemId, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: checklistKeys.detail(checklistId) }),
  })
}

export function useDeleteItem(checklistId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (itemId: string) => deleteItem(checklistId, itemId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: checklistKeys.detail(checklistId) }),
  })
}

export function useReorderItems(checklistId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ categoryId, payload }: { categoryId: string; payload: ReorderPayload }) =>
      reorderItems(checklistId, categoryId, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: checklistKeys.detail(checklistId) }),
  })
}

// NC Templates
export function useReplaceNcTemplates(checklistId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ itemId, payload }: { itemId: string; payload: ReplaceNcTemplatesPayload }) =>
      replaceNcTemplates(checklistId, itemId, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: checklistKeys.detail(checklistId) }),
  })
}

// Data Fields
export function useCreateDataField(checklistId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ itemId, payload }: { itemId: string; payload: CreateDataFieldPayload }) =>
      createDataField(checklistId, itemId, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: checklistKeys.detail(checklistId) }),
  })
}

export function useUpdateDataField(checklistId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ itemId, fieldId, payload }: { itemId: string; fieldId: string; payload: UpdateDataFieldPayload }) =>
      updateDataField(checklistId, itemId, fieldId, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: checklistKeys.detail(checklistId) }),
  })
}

export function useDeleteDataField(checklistId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ itemId, fieldId }: { itemId: string; fieldId: string }) =>
      deleteDataField(checklistId, itemId, fieldId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: checklistKeys.detail(checklistId) }),
  })
}

export function useReorderDataFields(checklistId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ itemId, orderedIds }: { itemId: string; orderedIds: string[] }) =>
      reorderDataFields(checklistId, itemId, { orderedIds }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: checklistKeys.detail(checklistId) }),
  })
}

// Checklist Scopes
export function useAssignChecklistScopes(checklistId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (scopeIds: string[]) => assignChecklistScopes(checklistId, scopeIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: checklistKeys.lists() })
      queryClient.invalidateQueries({ queryKey: checklistKeys.detail(checklistId) })
    },
  })
}

export function useRemoveChecklistScope(checklistId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (scopeId: string) => removeChecklistScope(checklistId, scopeId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: checklistKeys.lists() })
      queryClient.invalidateQueries({ queryKey: checklistKeys.detail(checklistId) })
    },
  })
}
